import React, { useState, useEffect, useRef } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import Input from '@/component/input/Input.tsx'
import { apiLogin } from '@/api/auth/login.ts'
import { useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useLoginStore } from '@/store/LoginStore.ts'
import CarearlyLogo from '@/assets/ic_carearly_logo.svg?react'
import SolidButton from '@/component/button/SolidButton.tsx'
import CheckBox from '@/component/checkbox/CheckBox.tsx'
import { isEmpty } from '@/util/strings.ts'
import { useTranslation } from 'react-i18next'
import { TURNSTILE_SITE_KEY } from '@/constant/constant.ts'

type IProps = {}

const Login: React.FC<IProps> = ({}) => {
    const { t } = useTranslation()
    const { login, setAutoLogin, isAutoLogin, isSaveID, setSaveID, savedID, accessToken } =
        useLoginStore()

    const [id, setId] = useState(savedID as string)
    const [password, setPassword] = useState('')
    const [turnstileRequired, setTurnstileRequired] = useState(false)
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
    const turnstileRef = useRef<HTMLDivElement>(null)
    const turnstileWidgetId = useRef<string | null>(null)
    const navigate = useNavigate()

    // 이미 로그인된 상태라면 메인으로 리다이렉트
    useEffect(() => {
        if (accessToken) {
            navigate('/', { replace: true })
        }
    }, [accessToken, navigate])

    // Turnstile 위젯 렌더링
    useEffect(() => {
        if (!turnstileRequired || !turnstileRef.current || !TURNSTILE_SITE_KEY) return

        const renderTurnstile = () => {
            if (window.turnstile && turnstileRef.current) {
                if (turnstileWidgetId.current) {
                    window.turnstile.remove(turnstileWidgetId.current)
                }
                turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: TURNSTILE_SITE_KEY,
                    callback: (token: string) => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(null)
                })
            }
        }

        if (window.turnstile) {
            renderTurnstile()
        } else if (!document.querySelector('script[src*="turnstile"]')) {
            const script = document.createElement('script')
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
            script.async = true
            script.onload = renderTurnstile
            document.head.appendChild(script)
        }
    }, [turnstileRequired])

    const onClickLogin = async () => {
        const result = await apiLogin({
            id,
            password,
            ...(turnstileToken && { turnstileToken })
        })

        if (result.message === 'TURNSTILE_REQUIRED' || result.turnstileRequired) {
            setTurnstileRequired(true)
            setTurnstileToken(null)
            toast.warning('보안 인증이 필요합니다')
            return
        }

        if (result.data && result.data.token) {
            await login(id, result.data.token)
            toast.success(t('login.submit.success'))
            navigate('/')
        } else {
            const failCount = result.loginFailCount
            if (failCount && failCount >= 3) {
                setTurnstileRequired(true)
            }
            // Turnstile 토큰은 1회용 — 실패 후 리셋하여 새 토큰 발급
            if (turnstileWidgetId.current && window.turnstile) {
                window.turnstile.reset(turnstileWidgetId.current)
                setTurnstileToken(null)
            }
            toast.error(result.message || t('login.submit.fail'))
        }
    }

    return (
        <>
            <PageConfig className={'mx-auto max-w-[300px] flex flex-col'}>
                <div className={'flex flex-col h-full justify-center items-center'}>
                    <div>
                        <div className={'flex items-center justify-center'}>
                            <Link to={'/'}>
                                <CarearlyLogo className={'w-[250px]'} />
                            </Link>
                        </div>
                        <Input
                            className={'mt-[40px]'}
                            inputClassName={'w-full'}
                            value={id}
                            onChanged={(text) => setId(text)}
                            inputType={'text'}
                            placeholder={t('login.id.placeholder')}
                        />
                        <Input
                            className={'mt-[10px]'}
                            inputClassName={'w-full'}
                            value={password}
                            inputType={'password'}
                            placeholder={t('login.password.placeholder')}
                            onPressEnter={onClickLogin}
                            onChanged={(text) => setPassword(text)}
                        />

                        {turnstileRequired && <div ref={turnstileRef} className={'mt-[10px]'} />}

                        <SolidButton
                            size={'medium'}
                            text={t('login.confirm')}
                            onClick={onClickLogin}
                            disabled={
                                isEmpty(id) ||
                                isEmpty(password) ||
                                (turnstileRequired && !turnstileToken)
                            }
                            className={'mt-[10px] w-full'}
                        />
                        <div className={'flex justify-center mt-[10px] gap-[25px]'}>
                            <CheckBox
                                id={'save_id'}
                                label={t('login.saveId')}
                                onChanged={(isChecked) => setSaveID(isChecked)}
                                value={isSaveID}
                            />
                            <CheckBox
                                id={'auto_login'}
                                label={t('login.autoLogin')}
                                onChanged={(isChecked) => setAutoLogin(isChecked)}
                                value={isAutoLogin}
                            />
                        </div>
                        <div className={'flex flex-col justify-end mt-[20px] text-center'}>
                            <p className={'text-body'}>{t('login.customerCenter.desc')}</p>
                            <p className={'text-body'}>{t('login.customerCenter.desc.phone')}</p>
                        </div>
                    </div>
                </div>
            </PageConfig>
        </>
    )
}

export default Login
